import { ComponentPropsWithRef, ReactNode, forwardRef } from 'react'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-regular-svg-icons'
import { Button } from '../../atoms/button'
import { mtc } from '../../../utils/style-helpers'
import { styles, actionsStyles } from './styles'

export interface StickyBottomNavProps extends Omit<
  ComponentPropsWithRef<'div'>,
  'onSubmit'
> {
  /** Content at the bar's start, where Back would sit. Below the buttons on mobile. */
  leading?: ReactNode
  onBack?: () => void
  onContinue?: () => void
  onSkip?: () => void
  backLabel?: string
  continueLabel?: string
  skipLabel?: string
  skipVariant?: 'secondary' | 'link'
  backDisabled?: boolean
  continueDisabled?: boolean
  skipDisabled?: boolean
  /** Pass `null` to omit the chevron. */
  backIcon?: IconDefinition | null
  /** Pass `null` to omit the chevron. */
  continueIcon?: IconDefinition | null
  /**
   * Distance (px) to inset the fixed nav from the left edge, e.g. so it
   * doesn't render underneath a resizable sidebar. Applies at every
   * breakpoint; if a caller's offset shouldn't apply on mobile (e.g. a
   * sidebar that collapses there), the caller varies the value itself.
   * (it's app-layout-specific, not a design-system concern).
   */
  leftOffset?: number
  /**
   * Distance (px) to inset the fixed nav from the right edge, e.g. so it
   * doesn't render underneath a floating widget. Applies at every
   * breakpoint, same as `leftOffset` — a fixed overlay a caller needs to
   * clear can easily still be present on mobile, so this component never
   * assumes otherwise.
   */
  rightOffset?: number
  /**
   * 0–100. Renders a thin filled line along the bar's top edge — a step
   * indicator combined into the same sticky surface instead of a second,
   * separately-stickied progress bar competing for viewport space.
   */
  progress?: number
  /** Accessible name for the progress line. Only read when `progress` is set. */
  progressLabel?: string
  /**
   * Below `sm`, sit at the end of the page instead of pinned to the viewport, so the bar is
   * reached by scrolling. `leftOffset`/`rightOffset` stop applying there along with it: a
   * static element ignores both.
   */
  unfixedOnMobile?: boolean
}

/**
 * A fixed-to-viewport-bottom Back/Continue(/Skip) bar for multi-step flows
 * (onboarding, wizards), optionally left in the page flow on mobile via
 * `unfixedOnMobile`. Fully generic: it has no built-in knowledge of any
 * specific app layout or floating widget. Use `leftOffset`/`rightOffset` to
 * clear whatever fixed elements a given app needs to clear.
 */
export const StickyBottomNav = forwardRef<HTMLDivElement, StickyBottomNavProps>(
  (
    {
      leading,
      onBack,
      onContinue,
      onSkip,
      backLabel = 'Back',
      continueLabel = 'Continue',
      skipLabel = 'Skip',
      skipVariant = 'secondary',
      backDisabled = false,
      continueDisabled = false,
      skipDisabled = false,
      backIcon = faChevronLeft,
      continueIcon = faChevronRight,
      leftOffset = 0,
      rightOffset = 0,
      progress,
      progressLabel = 'Progress',
      unfixedOnMobile = false,
      className,
      style,
      ...props
    },
    ref
  ) => {
    const clampedProgress =
      progress === undefined ? 0 : Math.max(0, Math.min(100, progress))
    return (
      <div
        ref={ref}
        className={mtc(styles({ unfixedOnMobile }), className)}
        style={{ left: leftOffset, right: rightOffset, ...style }}
        {...props}
      >
        {progress !== undefined && (
          <div
            className="absolute inset-x-0 top-0 h-1 bg-neutral-100"
            role="progressbar"
            aria-label={progressLabel}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={clampedProgress}
            aria-valuetext={`${clampedProgress}% complete`}
          >
            <div
              className="h-full bg-moss-200 transition-[width]"
              style={{ width: `${clampedProgress}%` }}
            />
          </div>
        )}
        {leading && <div className="mr-auto max-sm:mr-0">{leading}</div>}
        {onBack && (
          <Button
            className="mr-auto max-sm:mr-0"
            variant="secondary"
            onClick={onBack}
            disabled={backDisabled}
            iconLeft={backIcon ?? undefined}
          >
            {backLabel}
          </Button>
        )}
        <div className={actionsStyles()}>
          {onSkip && (
            <Button
              variant={skipVariant}
              onClick={onSkip}
              disabled={skipDisabled}
            >
              {skipLabel}
            </Button>
          )}
          {onContinue && (
            <Button
              variant="primary"
              onClick={onContinue}
              disabled={continueDisabled}
              iconRight={continueIcon ?? undefined}
            >
              {continueLabel}
            </Button>
          )}
        </div>
      </div>
    )
  }
)

StickyBottomNav.displayName = 'StickyBottomNav'
